import ForestGameAttributes, {InteractiveObject, UserGame} from '@/interfaces/entities/forest-game.interface';
import Feature from '@/interfaces/geo/feature.interface';
import GameUserPositionDistance from '@/interfaces/geo/game-user-position-distance';
import GameState from "@/enums/game-state-enum";

export class ForestGame {
    /**
     * @description This attribute need to be changed to trigger change detector.
     */
    public loader: boolean = false;

    constructor(private attributes: ForestGameAttributes) {
    }

    public id(): string {
        return this.attributes.id;
    }

    public lvmGeoId(): string {
        return this.attributes.lvmgeo_id;
    }

    public name(): string {
        return this.attributes.name;
    }

    public description(): string {
        return this.attributes.description;
    }

    public userGame(): UserGame {
        return this.attributes.userGame;
    }

    public userCompletedTasks(): string[] {
        return this.attributes.userCompletedTasks;
    }

    public correctAnswers(): number {
        return this.userGame().correctAnswersCount;
    }

    public deadline(): number {
        return this.attributes.deadline;
    }

    public deadlineUnits(): string {
        return this.attributes.deadline_unit;
    }

    public onlyForRegistered(): boolean {
        return Number(this.attributes.only_for_registered) === 1;
    }

    public interactiveObjects(): InteractiveObject[] {
        return JSON.parse(JSON.stringify(this.attributes.interactiveObjects));
    }

    public interactiveObject(id: string): InteractiveObject | null {
        return this.interactiveObjects().find((object: InteractiveObject) => object.id === id) || null;
    }

    public interactiveObjectsOrderedByDistance(distances: GameUserPositionDistance[]): InteractiveObject[] {
        return this.interactiveObjects()
            .sort((current: InteractiveObject, next: InteractiveObject) => {
                let currentDistance: number = 0;
                let nextDistance: number = 0;
                distances.forEach((distance: GameUserPositionDistance) => {
                    if (distance.pointId.toString() === current.lvmgeoId) {
                        currentDistance = distance.distance;
                    }
                    if (distance.pointId.toString() === next.lvmgeoId) {
                        nextDistance = distance.distance;
                    }
                });
                return currentDistance - nextDistance;
            });
    }

    public completedInteractiveObjects(): InteractiveObject[] {
        return this.interactiveObjects().filter((object: InteractiveObject) => {
            return this.userCompletedTasks().includes(object.id);
        });
    }

    public interactiveObjectForFeature(feature: Feature): InteractiveObject | null {
        return this.interactiveObjects()
            .find((object: InteractiveObject) => object.lvmgeoId === feature.values_.POINTID.toString()) || null;
    }

    public isStarted(): boolean {
        return Boolean(this.attributes.userGame.game_started);
    }

    public isEnded(): boolean {
        return this.isAllObjectsCompleted() || this.isTimeout();
    }

    public isTimeout(): boolean {
        let timeout = false;
        const deadline = this.attributes.userGame.deadline;
        if (!this.isAllObjectsCompleted() && deadline && deadline !== '0000-00-00 00:00:00') {
            timeout = new Date(deadline).getTime() - new Date().getTime() < 0;
        }
        return timeout;
    }

    public isGameOnTimer(): boolean {
        return this.attributes.deadline > 0;
    }

    public isInteractiveObjectCompleted(interactiveObject: InteractiveObject): boolean {
        return this.userCompletedTasks().includes(interactiveObject.id);
    }

    public isAllObjectsCompleted(): boolean {
        return this.interactiveObjects().length === this.userCompletedTasks().length;
    }

    public isMapCheckPointCompleted(checkPointFeature: Feature): boolean {
        return this.completedInteractiveObjects().some((object: InteractiveObject) => {
            return object.lvmgeoId === checkPointFeature.values_.POINTID.toString();
        });
    }

    public updateUserCompletedTasks(userCompletedTasks: string[]): void {
        this.attributes.userCompletedTasks = userCompletedTasks;
    }

    public updateGameStartTime(startTime: string): void {
        this.attributes.userGame.game_started = startTime;
    }

    public updateCorrectAnswers(answersCount: number): void {
        this.userGame().correctAnswersCount = answersCount;
    }

    public updateGameEndTime(endTime: string): void {
        this.attributes.userGame.deadline = endTime;
    }
}
